import { AppState, AppStateActionParams } from 'records/AppState';
import { BreadcrumbItem } from 'records/BreadcrumbItem';
import { StudioFolderImportRequest } from 'records/StudioFolderImportRequest';
import { StudioImportRequest } from 'records/StudioImportRequest';
import { StudioProjectFileData } from 'records/StudioProjectFile';
import { StudioProjectFolder } from 'records/StudioProjectFolder';
import {
  createNewStudioFolder,
  exportAllDataToStudioFolder,
  getStudioProjectFolder,
  getStudioProjects,
  importStudioFiles,
} from 'services/studioService';

export interface StudioActions {
  getStudioProjects: (actionParams: AppStateActionParams) => Promise<AppState>;
  getStudioProjectFolder: (
    actionParams: AppStateActionParams,
    projectId: string,
    folderId: string | number
  ) => Promise<AppState>;
  setStudioFolderName: (
    actionParams: AppStateActionParams,
    folderName: string
  ) => AppState;
  setStudioProjectId: (
    actionParams: AppStateActionParams,
    projectId: string
  ) => AppState;
  resetStudioView: (actionParams: AppStateActionParams) => AppState;
  importStudioFiles: (actionParams: AppStateActionParams) => Promise<AppState>;
  setStudioSelectedFiles: (
    actionParams: AppStateActionParams,
    selectedFiles: StudioProjectFileData[]
  ) => AppState;
  setStudioSelectedFolders: (
    actionParams: AppStateActionParams,
    selectedFolders: StudioProjectFolder[]
  ) => AppState;
  setStudioExportFolder: (
    actionParams: AppStateActionParams,
    folder: StudioProjectFolder | null
  ) => AppState;
  exportAllDataToStudio: (
    actionParams: AppStateActionParams,
    filesToExport: {
      drawings: boolean;
      punch: boolean;
      rfis: boolean;
      submittals: boolean;
      users: boolean;
    }
  ) => Promise<AppState>;
  createNewStudioFolder: (
    actionParams: AppStateActionParams,
    folderName: string
  ) => Promise<AppState>;
  clearStudioNavigation: (actionParams: AppStateActionParams) => AppState;
}

const studioActions: StudioActions = {
  getStudioProjects: async ({
    state,
  }: AppStateActionParams): Promise<AppState> => {
    const projects = await getStudioProjects();

    return state().setIn(['studioProjects'], projects);
  },

  getStudioProjectFolder: async (
    { state, actions }: AppStateActionParams,
    projectId: string,
    folderId: string | number
  ): Promise<AppState> => {
    const folder = await getStudioProjectFolder(projectId, +folderId);
    const folderName = state().get('studioCurrentFolderName');

    const newBreadcrumb = new BreadcrumbItem({
      driveId: projectId,
      folderId: folderId.toString(),
      label: folderName,
      key: folderId.toString(),
    });

    const { breadcrumbsItems } = await actions.setBreadcrumbs(newBreadcrumb);

    return state()
      .setIn(['studioCurrentFolder'], folder)
      .set('studioCurrentFolderId', folderId.toString())
      .set('breadcrumbsItems', breadcrumbsItems);
  },

  resetStudioView: ({ state }: AppStateActionParams): AppState => {
    return state()
      .set('studioCurrentFolder', new StudioProjectFolder())
      .set('studioCurrentFolderId', '')
      .set('studioCurrentFolderName', '');
  },

  setStudioFolderName: (
    { state }: AppStateActionParams,
    folderName: string
  ): AppState => {
    return state().set('studioCurrentFolderName', folderName);
  },

  importStudioFiles: async ({
    state,
    actions,
  }: AppStateActionParams): Promise<AppState> => {
    const studioSelectedFiles = state().get('studioSelectedFiles');
    const studioSelectedFolders = state().get('studioSelectedFolders');
    const currentProject = state().getProject().id;

    const updatedFiles = studioSelectedFiles.map(
      (f) =>
        new StudioImportRequest({
          id: f.id,
          studioprojectid: f.studioprojectid,
          fileName: f.name,
          filePath: f.path,
          fileSize: f.size,
        })
    );
    const studioFolders: StudioFolderImportRequest[] =
      studioSelectedFolders.map((f) => {
        return {
          studioprojectid: f.studioprojectid,
          id: f.id,
        } as StudioFolderImportRequest;
      });

    await importStudioFiles(currentProject, updatedFiles, studioFolders);
    window.setTimeout(actions.getProcessingStatuses, 400);

    return state()
      .set('studioSelectedFolders', [])
      .set('studioSelectedFiles', [])
      .set('studioCurrentFolder', new StudioProjectFolder())
      .set('studioCurrentFolderId', '')
      .set('studioCurrentFolderName', '');
  },

  setStudioSelectedFiles: (
    { state }: AppStateActionParams,
    selectedFiles: StudioProjectFileData[]
  ): AppState => {
    return state().set('studioSelectedFiles', selectedFiles);
  },

  setStudioSelectedFolders: (
    { state }: AppStateActionParams,
    selectedFolders: StudioProjectFolder[]
  ): AppState => {
    return state().set('studioSelectedFolders', selectedFolders);
  },

  setStudioProjectId: (
    { state }: AppStateActionParams,
    projectId: string
  ): AppState => {
    return state().set('studioCurrentProjectId', projectId);
  },

  setStudioExportFolder: (
    { state }: AppStateActionParams,
    folder: StudioProjectFolder | null
  ): AppState => {
    return state().set('studioExportFolder', folder);
  },

  exportAllDataToStudio: async (
    { state }: AppStateActionParams,
    filesToExport: {
      drawings: boolean;
      punch: boolean;
      rfis: boolean;
      submittals: boolean;
      users: boolean;
    }
  ): Promise<AppState> => {
    const parentFolderId = state().get('studioExportFolder')?.id || 0;

    const studioProjectId = state().get('studioCurrentProjectId');
    const projectId = state().getProject().id;

    await exportAllDataToStudioFolder(
      parentFolderId,
      studioProjectId,
      projectId,
      filesToExport.drawings,
      filesToExport.punch,
      filesToExport.rfis,
      filesToExport.submittals,
      filesToExport.users
    );

    return state();
  },
  createNewStudioFolder: async (
    { state, actions }: AppStateActionParams,
    folderName: string
  ): Promise<AppState> => {
    const studioProjectId = state().get('studioCurrentProjectId');
    const projectId = state().getProject().id;
    const parent = state().get('selectFolderCurrentId');

    await createNewStudioFolder(folderName, projectId, studioProjectId, parent);
    await actions.getStudioProjectFolder(studioProjectId, +parent);

    return state();
  },
  clearStudioNavigation({ state }: AppStateActionParams): AppState {
    return state()
      .set('studioCurrentFolder', new StudioProjectFolder())
      .set('studioCurrentFolderId', '')
      .set('studioCurrentProjectId', '')
      .set('selectFolderCurrentId', '')
      .set('studioCurrentFolderName', '');
  },
};

export default studioActions;
